import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import {
    GABLE_PICKER_KINDS,
    GablePendingQuestions,
    GablePickerKind,
    GableQuestion,
    GableQuestionOption,
} from '../../../../services/gable/gable-stream.service';
import { GableQuestionAnswer } from '../../../../services/gable/gable.service';
import { V2UnitSelectorComponent } from '../../shared/unit-selector/v2-unit-selector.component';
import { PeoplePickerComponent } from '../../shared/people-picker/people-picker.component';
import { PickerSelection } from '../../shared/people-picker/people-picker.types';
import { MatButtonIconSuffixDirective } from '../../shared/directives/button.directive';
import { Unit } from '../../../../models/entities/unit';

/**
 * Picker kinds answered by the shared people-picker (self-loads the org's
 * memberships when no [data] is supplied). `owner_picker` and
 * `account_picker` resolve to the same membership list — an "account" in
 * the ask_user_questions context is a member's account, and owners ARE
 * memberships; the model gets the picked membership ids either way.
 */
const PEOPLE_PICKER_KINDS: readonly string[] = ['member_picker', 'owner_picker', 'account_picker'];

/**
 * Per-question internal answer state. Keyed by question id. Values:
 *   - single_select: { selectedValue: string | null, otherActive: boolean, otherText: string }
 *   - multi_select:  { selectedValues: Set<string>,  otherActive: boolean, otherText: string }
 *   - text:          { text: string }
 *   - number:        { text: string } (kept as text until submit, parsed at confirm)
 *   - date:          { text: string } (yyyy-mm-dd)
 */
interface QuestionState {
    selectedValue?: string | null;
    selectedValues?: Set<string>;
    otherActive?: boolean;
    otherText?: string;
    text?: string;
    /** For `unit_picker` — picked units with their display title + id. */
    pickedUnits?: Array<{ id: number; title: string }>;
    /** For people kinds (member/owner/account) — picked memberships. */
    pickedPeople?: Array<{ id: number; name: string }>;
}

/**
 * Cowork-style structured-question card.
 *
 * Pagination one question at a time. Required questions block forward
 * navigation until answered. Keyboard:
 *   - 1–9          select option N (single) / toggle option N (multi)
 *   - ↑ / ↓        move focus between options or input
 *   - Enter        confirm + advance (or submit on the last question)
 *   - Spacebar     toggle focused option in multi_select
 *   - Tab          advance to next question (same as Enter when current is valid)
 *
 * Esc is intentionally NOT bound — required questions can't be skipped, and
 * the parent page exposes a Close (X) button gated on "all required filled."
 *
 * 👋 Heads-up on icons: this card uses the Phosphor webfont
 * (`<i class="ph-bold ph-…">`) for the Back/Next arrows, alongside the
 * V2 SVG sprite for the close/check icons. We're piloting Phosphor here
 * to evaluate it against the sprite pattern. Reviewer feedback to swap
 * the Phosphor `<i>` usages to the sprite can be deferred until the icon
 * pilot comparison lands.
 */
@Component({
    selector: 'app-question-card',
    templateUrl: './question-card.component.html',
    styleUrls: ['./question-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, V2UnitSelectorComponent, PeoplePickerComponent, MatButtonIconSuffixDirective],
})
export class QuestionCardComponent implements OnChanges, AfterViewInit {
    @Input() batch!: GablePendingQuestions;
    @Input() submitting = false;
    /** When true, Back on the first question emits `close` instead of being disabled. */
    @Input() backOnFirstPage = false;
    @Input() autoAdvance = true;

    @Output() readonly submitAnswers = new EventEmitter<GableQuestionAnswer[]>();
    @Output() readonly close = new EventEmitter<void>();

    /**
     * Free-form input on the currently rendered question (text/number/date).
     * Programmatically focused on view init + after each question advance so
     * the user lands typing-ready without needing two `autofocus` attributes
     * (only one is honored by the browser).
     */
    @ViewChild('freeFormInput') freeFormInputRef?: ElementRef<HTMLInputElement>;

    /** 0-indexed position in `batch.questions`. */
    currentIndex = 0;

    /** Answer state per question id. */
    state: Record<string, QuestionState> = {};

    /** For *_select kinds — index of the focused option row (0-based). */
    focusedOptionIndex = 0;

    /** Suppresses the keydown handler when the user is typing in a free-form field. */
    inputFocused = false;

    /**
     * Stable id for the card's heading element so `aria-labelledby` on
     * the role="group" container can point at it.
     */
    readonly cardHeadingId = `question-card-heading-${Math.random().toString(36).slice(2, 10)}`;

    constructor(private _cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['batch'] && this.batch) {
            this._initStateForBatch();
        }
    }

    ngAfterViewInit(): void {
        this._focusFreeFormInputIfPresent();
    }

    // =========================================================================
    // Template helpers
    // =========================================================================

    get currentQuestion(): GableQuestion | null {
        return this.batch?.questions[this.currentIndex] ?? null;
    }

    get isLastQuestion(): boolean {
        return this.currentIndex === (this.batch?.questions?.length ?? 0) - 1;
    }

    get totalQuestions(): number {
        return this.batch?.questions?.length ?? 0;
    }

    /** Number to display in the "X of Y" hint (1-based). */
    get displayIndex(): number {
        return this.currentIndex + 1;
    }

    /** Forward arrow / Submit button enabled? */
    get canAdvance(): boolean {
        const q = this.currentQuestion;
        if (q === null) {
            return false;
        }
        if (!q.required) {
            return true;
        }
        return this._isAnswered(q);
    }

    /** Close button (X) enabled? */
    get canClose(): boolean {
        if (!this.batch) {
            return true;
        }
        return this.batch.questions.every((q) => !q.required || this._isAnswered(q));
    }

    /** All required answered AND we're on the last question? Show "Submit". */
    get canSubmit(): boolean {
        if (!this.batch) {
            return false;
        }
        return this.batch.questions.every((q) => !q.required || this._isAnswered(q));
    }

    trackByOptionValue(_: number, opt: GableQuestionOption): string {
        return opt.value;
    }

    /** Visible option list — handles `allow_other` virtual row. */
    optionsFor(q: GableQuestion): GableQuestionOption[] {
        return q.options ?? [];
    }

    /** Whether the focused option is the "Something else" virtual row. */
    isOtherRowFocused(q: GableQuestion): boolean {
        const opts = this.optionsFor(q);
        return q.allow_other === true && this.focusedOptionIndex === opts.length;
    }

    isOptionSelected(q: GableQuestion, opt: GableQuestionOption): boolean {
        const s = this.state[q.id];
        if (q.kind === 'single_select') {
            return s?.selectedValue === opt.value;
        }
        if (q.kind === 'multi_select') {
            return s?.selectedValues?.has(opt.value) ?? false;
        }
        return false;
    }

    multiSelectionCount(q: GableQuestion): number {
        const s = this.state[q.id];
        if (q.kind !== 'multi_select') {
            return 0;
        }
        let count = s?.selectedValues?.size ?? 0;
        if (s?.otherActive && (s.otherText ?? '').trim() !== '') {
            count += 1;
        }
        return count;
    }

    // =========================================================================
    // Click handlers (kind-specific)
    // =========================================================================

    onSingleSelectClick(q: GableQuestion, opt: GableQuestionOption): void {
        const s = this.state[q.id] ?? {};
        s.selectedValue = opt.value;
        s.otherActive = false;
        s.otherText = '';
        this.state = { ...this.state, [q.id]: s };
        this._cdr.markForCheck();
    }

    onOptionPicked(): void {
        if (this.autoAdvance) {
            this.next();
        }
    }

    onMultiSelectToggle(q: GableQuestion, opt: GableQuestionOption): void {
        const s = this.state[q.id] ?? { selectedValues: new Set<string>() };
        if (!s.selectedValues) {
            s.selectedValues = new Set<string>();
        }
        if (s.selectedValues.has(opt.value)) {
            s.selectedValues.delete(opt.value);
        } else {
            s.selectedValues.add(opt.value);
        }
        // Force a new Set reference so OnPush picks up the mutation.
        s.selectedValues = new Set(s.selectedValues);
        this.state = { ...this.state, [q.id]: s };
        this._cdr.markForCheck();
    }

    onActivateOther(q: GableQuestion): void {
        const s = this.state[q.id] ?? {};
        s.otherActive = true;
        if (s.otherText === undefined) {
            s.otherText = '';
        }
        // For single-select, picking "Other" implicitly clears the prior
        // option selection — the answer is now the free-form text.
        if (q.kind === 'single_select') {
            s.selectedValue = null;
        }
        this.state = { ...this.state, [q.id]: s };
        this._cdr.markForCheck();
    }

    onOtherTextInput(q: GableQuestion, value: string): void {
        const s = this.state[q.id] ?? {};
        s.otherText = value;
        this.state = { ...this.state, [q.id]: s };
        this._cdr.markForCheck();
    }

    // =========================================================================
    // Entity pickers
    // =========================================================================

    pickedUnitsFor(q: GableQuestion): Array<{ id: number; title: string }> {
        return this.state[q.id]?.pickedUnits ?? [];
    }

    /** Numeric IDs of currently-picked units; passed into the inline selector. */
    pickedUnitIdsFor(q: GableQuestion): number[] {
        return this.pickedUnitsFor(q).map((u) => u.id);
    }

    /**
     * `unit_picker` and the people kinds (member/owner/account) are wired
     * to real selectors. The remaining picker kinds are stubs — they can't
     * be answered yet, so they're effectively Skip-only (the model is told
     * not to mark them `required: true`).
     */
    isPickerStubbed(q: GableQuestion): boolean {
        return GABLE_PICKER_KINDS.includes(q.kind as GablePickerKind)
            && q.kind !== 'unit_picker'
            && !PEOPLE_PICKER_KINDS.includes(q.kind);
    }

    // ---- People pickers (member / owner / account) ----

    pickedPeopleFor(q: GableQuestion): Array<{ id: number; name: string }> {
        return this.state[q.id]?.pickedPeople ?? [];
    }

    /** Membership ids currently picked; passed into the people-picker. */
    pickedPersonIdsFor(q: GableQuestion): number[] {
        return this.pickedPeopleFor(q).map((p) => p.id);
    }

    /**
     * Mirror of {@link onUnitSelectionChanged} for the shared people-picker:
     * keep lightweight `{id, name}` pairs in per-question state so the
     * answer payload and summary don't retain full PickerPerson models.
     * Free-typed emails can't occur here (allowFreeformEmail stays off).
     */
    onPeopleSelectionChanged(q: GableQuestion, selections: PickerSelection[]): void {
        const picked = (selections ?? [])
            .filter((sel): sel is Extract<PickerSelection, { type: 'person' }> => sel.type === 'person')
            .map((sel) => ({ id: Number(sel.person.id), name: sel.person.name }));
        const s = this.state[q.id] ?? {};
        s.pickedPeople = picked;
        this.state = { ...this.state, [q.id]: s };
        this._cdr.markForCheck();
    }

    /** Human-readable entity label for the stub placeholder ("vendors", "members", ...). */
    pickerEntityLabel(q: GableQuestion): string {
        const map: Record<GablePickerKind, string> = {
            unit_picker: 'units',
            vendor_picker: 'vendors',
            member_picker: 'members',
            owner_picker: 'owners',
            document_picker: 'documents',
            request_picker: 'requests',
            violation_picker: 'violations',
            charge_picker: 'charges',
            category_picker: 'categories',
            account_picker: 'accounts',
        };
        return map[q.kind as GablePickerKind] ?? 'items';
    }

    /**
     * Inline `app-v2-unit-selector` emits the full Unit[] on every
     * checkbox change. We mirror it into per-question state as
     * `{id, title}` pairs so the summary list + answer payload stay
     * lightweight (we don't need to retain the full Unit model).
     */
    onUnitSelectionChanged(q: GableQuestion, units: Unit[]): void {
        const picked = (units ?? []).map((u) => ({
            id: Number(u.id),
            title: (u.title ?? '') || `Unit #${u.id}`,
        }));
        const s = this.state[q.id] ?? {};
        s.pickedUnits = picked;
        this.state = { ...this.state, [q.id]: s };
        this._cdr.markForCheck();
    }

    onTextInput(q: GableQuestion, value: string): void {
        const s = this.state[q.id] ?? {};
        s.text = value;
        this.state = { ...this.state, [q.id]: s };
        this._cdr.markForCheck();
    }

    onInputFocus(): void {
        this.inputFocused = true;
    }

    onInputBlur(): void {
        this.inputFocused = false;
    }

    // =========================================================================
    // Navigation
    // =========================================================================

    next(): void {
        if (!this.canAdvance) {
            return;
        }
        if (this.isLastQuestion) {
            this._submit();
            return;
        }
        this.currentIndex += 1;
        this.focusedOptionIndex = 0;
        this._cdr.markForCheck();
        // Defer to next microtask so the *ngSwitch has time to render the
        // new question's input before we try to focus it.
        queueMicrotask(() => this._focusFreeFormInputIfPresent());
    }

    prev(): void {
        if (this.currentIndex === 0) {
            if (this.backOnFirstPage && !this.submitting) {
                this.close.emit();
            }
            return;
        }
        this.currentIndex -= 1;
        this.focusedOptionIndex = 0;
        this._cdr.markForCheck();
        queueMicrotask(() => this._focusFreeFormInputIfPresent());
    }

    /**
     * Focus the free-form input (text / number / date / "Something else"
     * textbox) when the current question is one of those kinds. Replaces
     * the previous HTML-level `autofocus` attributes — two `autofocus` on
     * the same page only honor one, and a programmatic focus is more
     * reliable across template branches.
     */
    private _focusFreeFormInputIfPresent(): void {
        const el = this.freeFormInputRef?.nativeElement;
        if (el) {
            el.focus();
        }
    }

    onCloseClick(): void {
        if (!this.canClose || this.submitting) {
            return;
        }
        this.close.emit();
    }

    // =========================================================================
    // Keyboard
    // =========================================================================

    @HostListener('keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        const q = this.currentQuestion;
        if (q === null) {
            return;
        }

        // When a free-form input is focused, only Enter / Shift+Enter are
        // worth intercepting — let everything else (including 1-9 typing)
        // fall through to the input.
        if (this.inputFocused) {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this.next();
            }
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this._moveFocus(1, q);
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this._moveFocus(-1, q);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            this._handleEnter(q);
            return;
        }
        if (event.key === ' ' || event.code === 'Space') {
            if (q.kind === 'multi_select') {
                event.preventDefault();
                this._toggleFocused(q);
            }
            return;
        }
        if (event.key === 'Tab' && !event.shiftKey && this.canAdvance) {
            event.preventDefault();
            this.next();
            return;
        }
        // 1-9 → select / toggle the Nth option (single / multi only)
        if (/^[1-9]$/.test(event.key) && (q.kind === 'single_select' || q.kind === 'multi_select')) {
            const idx = Number(event.key) - 1;
            const opts = this.optionsFor(q);
            if (idx < opts.length) {
                event.preventDefault();
                this.focusedOptionIndex = idx;
                if (q.kind === 'single_select') {
                    this.onSingleSelectClick(q, opts[idx]);
                    this.onOptionPicked();
                } else {
                    this.onMultiSelectToggle(q, opts[idx]);
                }
            }
        }
    }

    private _handleEnter(q: GableQuestion): void {
        if (q.kind === 'single_select') {
            const opts = this.optionsFor(q);
            if (this.isOtherRowFocused(q)) {
                this.onActivateOther(q);
                return;
            }
            const focused = opts[this.focusedOptionIndex];
            if (focused) {
                this.onSingleSelectClick(q, focused);
                this.next();
            }
            return;
        }
        if (q.kind === 'multi_select') {
            const opts = this.optionsFor(q);
            if (this.isOtherRowFocused(q)) {
                this.onActivateOther(q);
                return;
            }
            const focused = opts[this.focusedOptionIndex];
            if (focused) {
                this._toggleFocused(q);
                // Cowork: Enter on an unchecked row checks it and advances.
                if (this.isOptionSelected(q, focused)) {
                    this.next();
                }
            }
            return;
        }
        // text / number / date / unknown — just try to advance.
        this.next();
    }

    private _moveFocus(delta: number, q: GableQuestion): void {
        if (q.kind !== 'single_select' && q.kind !== 'multi_select') {
            return;
        }
        const opts = this.optionsFor(q);
        const max = opts.length + (q.allow_other ? 1 : 0) - 1;
        if (max < 0) {
            return;
        }
        let next = this.focusedOptionIndex + delta;
        if (next < 0) {
            next = max;
        }
        if (next > max) {
            next = 0;
        }
        this.focusedOptionIndex = next;
        this._cdr.markForCheck();
    }

    private _toggleFocused(q: GableQuestion): void {
        const opts = this.optionsFor(q);
        const focused = opts[this.focusedOptionIndex];
        if (focused) {
            this.onMultiSelectToggle(q, focused);
        }
    }

    // =========================================================================
    // Submit
    // =========================================================================

    private _submit(): void {
        if (!this.canSubmit || this.submitting) {
            return;
        }
        const answers: GableQuestionAnswer[] = this.batch.questions.map((q) =>
            this._answerFor(q),
        );
        this.submitAnswers.emit(answers);
    }

    private _answerFor(q: GableQuestion): GableQuestionAnswer {
        const s = this.state[q.id] ?? {};
        if (q.kind === 'single_select') {
            if (s.otherActive) {
                return { id: q.id, value: (s.otherText ?? '').trim() };
            }
            return { id: q.id, value: s.selectedValue ?? '' };
        }
        if (q.kind === 'multi_select') {
            const baseValues = s.selectedValues ? Array.from(s.selectedValues) : [];
            const extras = s.otherActive && (s.otherText ?? '').trim() !== ''
                ? [(s.otherText ?? '').trim()]
                : [];
            return { id: q.id, value: [...baseValues, ...extras] };
        }
        if (q.kind === 'number') {
            const num = Number((s.text ?? '').trim());
            return { id: q.id, value: Number.isFinite(num) ? num : 0 };
        }
        if (q.kind === 'unit_picker') {
            // Send numeric IDs as strings so the answer fits the existing
            // `string | string[] | number` shape. The orchestrator parses
            // them back to ints when synthesizing the answer message.
            const ids = (s.pickedUnits ?? []).map((u) => String(u.id));
            return { id: q.id, value: ids };
        }
        if (PEOPLE_PICKER_KINDS.includes(q.kind)) {
            // Same string-id convention as unit_picker — membership ids.
            const ids = (s.pickedPeople ?? []).map((p) => String(p.id));
            return { id: q.id, value: ids };
        }
        // text, date, stubbed pickers, unknown fallback — string passthrough.
        return { id: q.id, value: (s.text ?? '').trim() };
    }

    // =========================================================================
    // State init / answeredness
    // =========================================================================

    private _initStateForBatch(): void {
        const next: Record<string, QuestionState> = {};
        for (const q of this.batch.questions) {
            const s: QuestionState = {};
            // Apply backend-supplied defaults.
            if (q.default !== undefined && q.default !== null) {
                if (q.kind === 'single_select') {
                    s.selectedValue = typeof q.default === 'string' ? q.default : null;
                } else if (q.kind === 'multi_select') {
                    if (Array.isArray(q.default)) {
                        s.selectedValues = new Set(q.default);
                    } else if (typeof q.default === 'string') {
                        s.selectedValues = new Set([q.default]);
                    }
                } else {
                    s.text = typeof q.default === 'string' ? q.default : '';
                }
            } else {
                if (q.kind === 'multi_select') {
                    s.selectedValues = new Set<string>();
                } else if (q.kind === 'single_select') {
                    s.selectedValue = null;
                } else if (q.kind === 'unit_picker') {
                    s.pickedUnits = [];
                } else {
                    s.text = '';
                }
            }
            next[q.id] = s;
        }
        this.state = next;
        this.currentIndex = 0;
        this.focusedOptionIndex = 0;

        // Defense-in-depth: empty options on a *_select means the backend
        // gave us something invalid — log + flag so we can fall back to
        // text in the template.
        for (const q of this.batch.questions) {
            if (
                (q.kind === 'single_select' || q.kind === 'multi_select')
                && (!q.options || q.options.length === 0)
            ) {
                console.warn(
                    `[QuestionCardComponent] Question "${q.id}" is ${q.kind} but has no options; falling back to text input.`,
                );
            }
            const isPicker = GABLE_PICKER_KINDS.includes(q.kind as GablePickerKind);
            if (
                q.kind !== 'single_select'
                && q.kind !== 'multi_select'
                && q.kind !== 'text'
                && q.kind !== 'textarea'
                && q.kind !== 'number'
                && q.kind !== 'date'
                && !isPicker
            ) {
                console.warn(
                    `[QuestionCardComponent] Unknown question kind "${q.kind}" on question "${q.id}"; rendering as text.`,
                );
            }
        }
    }

    private _isAnswered(q: GableQuestion): boolean {
        const s = this.state[q.id];
        if (!s) {
            return false;
        }
        if (q.kind === 'single_select') {
            if (s.otherActive) {
                return (s.otherText ?? '').trim() !== '';
            }
            return s.selectedValue !== null && s.selectedValue !== undefined && s.selectedValue !== '';
        }
        if (q.kind === 'multi_select') {
            const baseCount = s.selectedValues?.size ?? 0;
            const otherCount = s.otherActive && (s.otherText ?? '').trim() !== '' ? 1 : 0;
            return baseCount + otherCount > 0;
        }
        if (q.kind === 'number') {
            const trimmed = (s.text ?? '').trim();
            if (trimmed === '') {
                return false;
            }
            return Number.isFinite(Number(trimmed));
        }
        if (q.kind === 'unit_picker') {
            return (s.pickedUnits ?? []).length > 0;
        }
        if (PEOPLE_PICKER_KINDS.includes(q.kind)) {
            return (s.pickedPeople ?? []).length > 0;
        }
        // Stubbed picker kinds: nothing to capture in the UI yet, so they
        // never count as answered. Backend should NOT mark them required.
        if (this.isPickerStubbed(q)) {
            return false;
        }
        return (s.text ?? '').trim() !== '';
    }

    /**
     * Effective render kind — falls back to `text` for unknown kinds and
     * for `*_select` with empty options. Used by the template's *ngSwitch.
     */
    effectiveKind(q: GableQuestion): string {
        if (
            (q.kind === 'single_select' || q.kind === 'multi_select')
            && (!q.options || q.options.length === 0)
        ) {
            return 'text';
        }
        if (q.kind === 'unit_picker') {
            return 'unit_picker';
        }
        if (PEOPLE_PICKER_KINDS.includes(q.kind)) {
            return 'people_picker';
        }
        if (GABLE_PICKER_KINDS.includes(q.kind as GablePickerKind)) {
            return 'picker_stub';
        }
        if (
            q.kind !== 'single_select'
            && q.kind !== 'multi_select'
            && q.kind !== 'text'
            && q.kind !== 'textarea'
            && q.kind !== 'number'
            && q.kind !== 'date'
        ) {
            return 'text';
        }
        return q.kind;
    }
}
